/**
 * ROUND 13 E4 -- render a refusal in the reader's own language.
 *
 * The backend composes a sentence too, and that sentence is not a
 * fallback nobody maintains: it is what an API consumer, a log line
 * and a test read, and it has to stand on its own. What it cannot do
 * is speak Arabic, because it is composed in the backend where no
 * catalogue can reach it -- which is why these refusals scored zero on
 * translation readiness.
 *
 * So a refusal that names a catalogue key is rendered from the
 * catalogue with the server's own values interpolated, and one that
 * does not is rendered as the server wrote it. Nothing is invented and
 * nothing is lost.
 */
import { listOf } from "shared/i18n/listOf";
import { i18n } from "shared/i18n";
import type { TFunction } from "i18next";

import { ApiError } from "shared/api/client";
import { roleLabel, stateLabel } from "shared/vocab/workflowState";

export function refusalMessage(t: TFunction, error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : String(error);
  }
  if (!error.code) return error.detail;
  // `refusal.seat_required` -> `refusals.seatRequired`. The key is part
  // of the API, so the catalogue is reached through a mapping rather
  // than by giving the wire a JavaScript-shaped name.
  const leaf = error.code
    .slice("refusal.".length)
    .replace(/_(.)/g, (_m, c: string) => c.toUpperCase());
  // defaultValue is the SERVER's sentence: a key the catalogue has not
  // learned yet must not leave the reader with a dotted token where a
  // refusal should be.
  // Round 18 A36 -- a refusal naming the person holding a seat says it
  // where the catalogue has the sentence for it.
  const held = (error.params?.holder ?? "").trim() !== "";
  // Round 19 N14 -- several holding the seat say how many, where the
  // catalogue has the sentence for it.
  const several = Number(error.params?.holders ?? 0) > 1;
  const key =
    held && i18n.exists(`refusals.${leaf}Held`)
      ? `refusals.${leaf}Held`
      : several && i18n.exists(`refusals.${leaf}Several`)
        ? `refusals.${leaf}Several`
        : `refusals.${leaf}`;
  return t(key, {
    ...(error.params ?? {}),
    ...wordedParams(error.params ?? {}, t),
    defaultValue: error.detail,
  });
}

/**
 * ROUND 15 I7 -- a refusal's values that are VOCABULARY, not data. The
 * server interpolates its own English ("This plan is in review"); the
 * catalogue sentence takes the same key and renders the state and the
 * seat in the reader's words. Data values (a name, a figure, an id)
 * pass through as they are.
 */
function wordedParams(
  params: Record<string, unknown>,
  t: TFunction,
): Record<string, string> {
  const out: Record<string, string> = {};
  // Round 16 I8 -- a register class is vocabulary too ("customer",
  // "internal"); the floor list's refusal named both in English.
  for (const [wire, key] of [
    ["item_class", "itemClass"],
    ["service_class", "serviceClass"],
  ] as const) {
    const v = params[wire];
    if (typeof v === "string")
      out[key] = t(`modules.foundation.floorList.class.${v}`, {
        defaultValue: v,
      }).toLowerCase();
  }
  if (typeof params.state === "string") out.state = stateLabel(params.state);
  if (typeof params.seat === "string") out.seat = roleLabel(params.seat).toLowerCase();
  if (typeof params.role === "string") out.role = roleLabel(params.role);
  /* Round 16 I12 -- the seats and the actions as codes, worded here, so
     no part of a workflow refusal arrives in English. Present only on
     refusals that send them; the English parameters beside them stay
     for a log line and an API consumer. */
  const codes = (v: unknown): string[] | null =>
    typeof v === "string" ? v.split(",").filter(Boolean) : null;
  const needed = codes(params.needed_codes);
  if (needed)
    out.needed = needed.length
      ? listOf(needed.map(roleLabel), "disjunction")
      : t("refusals.seatAdministrator");
  const held = codes(params.held_codes);
  if (held) {
    out.held = listOf(held.map(roleLabel), "conjunction");
    if (held.length === 0) out.context = "none";
  }
  const actionWord = (a: string) => t(`refusals.action.${a}`, { defaultValue: a });
  if (typeof params.action_code === "string")
    out.action = actionWord(params.action_code);
  const allowed = codes(params.allowed_codes);
  if (allowed) {
    out.allowed = listOf(allowed.map(actionWord), "disjunction");
    if (allowed.length === 0) out.context = "nothing";
  }
  return out;
}
